import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { z } from 'zod';
import { ConfigurationChangelogVersionEvent } from '@rossum/api-client/configurationChangelog';
import { Hook } from '@rossum/api-client/hooks';
import { User } from '@rossum/api-client/users';
import {
  FilterDateRange,
  FilterMultiSelect,
  FilterNumberValues,
} from '@rossum/components/filtering/components';
import { createSettings } from '@rossum/components/filtering/helpers';
import { FilterComponentProps } from '@rossum/components/filtering/types';
import {
  convertFromUTC,
  convertToUTC,
  formatDateHoursSecondsToQuery,
  formatDateTimeWithSecondsPickerValue,
} from '../../../lib/dateUtils';
import { WorkspaceWithQueues } from '../../queues/hooks/useWorkspacesWithQueues';
import { getUserName } from './useConfigurationChangelogUsers';

// Triggers are an implementation detail of extensions and confused users, so
// they cannot be filtered for. Their entries still show up in the list.
const OBJECT_TYPE_OPTIONS = [
  'hook',
  'schema',
  'queue',
  'rule',
  'email_template',
  'workspace',
  'engine',
] as const;
type ObjectTypeOption = (typeof OBJECT_TYPE_OPTIONS)[number];

const VERSION_EVENT_OPTIONS = ['create', 'update', 'delete'] as const;

const objectTypeLabelKeys = {
  hook: 'features.configurationChangelog.objectType.hook',
  schema: 'features.configurationChangelog.objectType.schema',
  queue: 'features.configurationChangelog.objectType.queue',
  rule: 'features.configurationChangelog.objectType.rule',
  email_template: 'features.configurationChangelog.objectType.email_template',
  workspace: 'features.configurationChangelog.objectType.workspace',
  engine: 'features.configurationChangelog.objectType.engine',
} as const satisfies Record<ObjectTypeOption, string>;

const versionEventLabelKeys = {
  create: 'features.configurationChangelog.versionEvent.create',
  update: 'features.configurationChangelog.versionEvent.update',
  delete: 'features.configurationChangelog.versionEvent.delete',
} as const satisfies Record<ConfigurationChangelogVersionEvent, string>;

const idListParser = z.coerce
  .string()
  .transform(val => val.split(',').map(Number))
  .pipe(z.number().array())
  .optional()
  .catch(undefined);

export const configurationChangelogQueryParser = z.object({
  search: z.coerce.string().optional(),
  hook: idListParser,
  queue: idListParser,
  versionCreatedAtAfter: z.coerce.string().optional(),
  versionCreatedAtBefore: z.coerce.string().optional(),
  objectType: z.coerce
    .string()
    .transform(val => val.split(','))
    .pipe(z.enum(OBJECT_TYPE_OPTIONS).array())
    .optional()
    .catch(undefined),
  versionEvent: z.coerce
    .string()
    .transform(val => val.split(','))
    .pipe(z.enum(VERSION_EVENT_OPTIONS).array())
    .optional()
    .catch(undefined),
  modifierId: idListParser,
  objectId: idListParser,
});

export type ConfigurationChangelogQuery = Partial<
  z.infer<typeof configurationChangelogQueryParser>
>;

type UseConfigurationChangelogFilterSettingsProps = {
  setFilters: (state: ConfigurationChangelogQuery) => void;
  usersById: Map<number, User>;
  areUsersLoading: boolean;
  hooks: Hook[] | undefined;
  areHooksLoading: boolean;
  workspaces: WorkspaceWithQueues[] | undefined;
  areQueuesLoading: boolean;
};

const sortByName = <T extends { name: string }>(items: T[]) =>
  [...items].sort((a, b) => a.name.localeCompare(b.name));

export const useConfigurationChangelogFilterSettings = ({
  setFilters,
  usersById,
  areUsersLoading,
  hooks,
  areHooksLoading,
  workspaces,
  areQueuesLoading,
}: UseConfigurationChangelogFilterSettingsProps) => {
  const intl = useIntl();

  const queuesById = useMemo(
    () =>
      new Map(
        (workspaces ?? [])
          .flatMap(workspace => workspace.queues)
          .map(queue => [queue.id, queue])
      ),
    [workspaces]
  );
  const queueOptions = useMemo(
    () =>
      (workspaces ?? []).map(workspace => ({
        id: String(workspace.id),
        label: workspace.name,
        options: workspace.queues.map(queue => String(queue.id)),
      })),
    [workspaces]
  );
  const getQueueName = useCallback(
    (id: string) => queuesById.get(Number(id))?.name ?? id,
    [queuesById]
  );

  // The API takes one object type plus IDs. The Extension and Queue pickers
  // fill both, so they replace each other and the generic Object type and
  // Object ID filters, and those two replace the pickers.
  const setPickerFilter = useCallback(
    (picker: Pick<ConfigurationChangelogQuery, 'hook' | 'queue'>) =>
      setFilters({
        hook: undefined,
        queue: undefined,
        objectType: undefined,
        objectId: undefined,
        ...picker,
      }),
    [setFilters]
  );
  const setGenericObjectFilter = useCallback(
    (filter: Pick<ConfigurationChangelogQuery, 'objectType' | 'objectId'>) =>
      setFilters({ hook: undefined, queue: undefined, ...filter }),
    [setFilters]
  );

  const hooksById = useMemo(
    () => new Map((hooks ?? []).map(hook => [hook.id, hook])),
    [hooks]
  );
  const hookOptions = useMemo(
    () => sortByName(hooks ?? []).map(hook => String(hook.id)),
    [hooks]
  );
  const getHookName = useCallback(
    (id: string) => hooksById.get(Number(id))?.name ?? id,
    [hooksById]
  );

  const modifierOptions = useMemo(
    () =>
      Array.from(usersById.values())
        .map(user => ({
          id: user.id,
          label: getUserName(user),
          deleted: user.deleted,
        }))
        .sort(
          (a, b) =>
            Number(a.deleted) - Number(b.deleted) ||
            a.label.localeCompare(b.label)
        )
        .map(({ id }) => `${id}`),
    [usersById]
  );

  const getModifierLabel = useCallback(
    (id: string) => {
      const user = usersById.get(Number(id));
      if (!user) return id;
      const name = getUserName(user);
      return user.deleted
        ? intl.formatMessage(
            { id: 'features.configurationChangelog.rows.modifierDeleted' },
            { name }
          )
        : name;
    },
    [usersById, intl]
  );

  return [
    createSettings({
      filterKey: 'hook',
      options: hookOptions,
      optionsAreLoading: areHooksLoading,
      name: intl.formatMessage({
        id: 'features.configurationChangelog.filters.hook',
      }),
      getOptionLabel: (id: string) => `${id}: ${getHookName(id)}`,
      getChipLabel: (value: string[]) => value.map(getHookName).join(', '),
      valueGetter: (currentValue: ConfigurationChangelogQuery) =>
        currentValue?.hook?.map(String),
      setFilter: (value: string[] | undefined) =>
        setPickerFilter({
          hook: value?.length ? value.map(Number) : undefined,
        }),
      Component: FilterMultiSelect,
    }),
    createSettings({
      filterKey: 'queue',
      options: queueOptions,
      optionsAreLoading: areQueuesLoading,
      name: intl.formatMessage({
        id: 'features.configurationChangelog.filters.queue',
      }),
      getOptionLabel: (id: string) => `${id}: ${getQueueName(id)}`,
      getChipLabel: (value: string[]) => value.map(getQueueName).join(', '),
      valueGetter: (currentValue: ConfigurationChangelogQuery) =>
        currentValue?.queue?.map(String),
      setFilter: (value: string[] | undefined) =>
        setPickerFilter({
          queue: value?.length ? value.map(Number) : undefined,
        }),
      Component: FilterMultiSelect,
    }),
    createSettings({
      filterKey: 'objectType',
      options: [...OBJECT_TYPE_OPTIONS],
      optionsAreLoading: false,
      name: intl.formatMessage({
        id: 'features.configurationChangelog.filters.objectType',
      }),
      getOptionLabel: (option: string) => {
        const key = OBJECT_TYPE_OPTIONS.find(o => o === option);
        return key
          ? intl.formatMessage({ id: objectTypeLabelKeys[key] })
          : option;
      },
      getChipLabel: (value: ObjectTypeOption[]) =>
        value
          .map(v =>
            intl.formatMessage({
              id: objectTypeLabelKeys[v],
            })
          )
          .join(', '),
      valueGetter: (currentValue: ConfigurationChangelogQuery) =>
        currentValue?.objectType,
      setFilter: value => setGenericObjectFilter({ objectType: value }),
      Component: FilterMultiSelect,
    }),
    createSettings({
      filterKey: 'versionEvent',
      options: [...VERSION_EVENT_OPTIONS],
      optionsAreLoading: false,
      name: intl.formatMessage({
        id: 'features.configurationChangelog.filters.versionEvent',
      }),
      getOptionLabel: (option: string) => {
        const key = VERSION_EVENT_OPTIONS.find(o => o === option);
        return key
          ? intl.formatMessage({ id: versionEventLabelKeys[key] })
          : option;
      },
      getChipLabel: (value: ConfigurationChangelogVersionEvent[]) =>
        value
          .map(v =>
            intl.formatMessage({
              id: versionEventLabelKeys[v],
            })
          )
          .join(', '),
      valueGetter: (currentValue: ConfigurationChangelogQuery) =>
        currentValue?.versionEvent,
      setFilter: value => setFilters({ versionEvent: value }),
      Component: FilterMultiSelect,
    }),
    createSettings({
      filterKey: 'versionCreatedAt',
      options: [],
      name: intl.formatMessage({
        id: 'features.configurationChangelog.filters.versionCreatedAt',
      }),
      getChipLabel: (v: [Date | null, Date | null]) =>
        v[0] && v[1]
          ? formatDateTimeWithSecondsPickerValue(v[0], v[1], intl.locale)
          : 'N/A',
      valueGetter: ({
        versionCreatedAtAfter,
        versionCreatedAtBefore,
      }: ConfigurationChangelogQuery) => {
        if (!versionCreatedAtAfter || !versionCreatedAtBefore) return undefined;

        const value: [Date | null, Date | null] = [
          convertFromUTC(new Date(versionCreatedAtAfter)),
          convertFromUTC(new Date(versionCreatedAtBefore)),
        ];
        return value;
      },
      setFilter: value => {
        if (!value)
          return setFilters({
            versionCreatedAtAfter: undefined,
            versionCreatedAtBefore: undefined,
          });

        const [from, to] = value;

        return setFilters({
          versionCreatedAtAfter: from
            ? formatDateHoursSecondsToQuery(convertToUTC(from))
            : undefined,
          versionCreatedAtBefore: to
            ? formatDateHoursSecondsToQuery(convertToUTC(to))
            : undefined,
        });
      },
      Component: (
        props: FilterComponentProps<[Date | null, Date | null] | undefined>
      ) => <FilterDateRange {...props} isRange locale={intl.locale} />,
    }),
    createSettings({
      filterKey: 'modifierId',
      options: modifierOptions,
      optionsAreLoading: areUsersLoading,
      name: intl.formatMessage({
        id: 'features.configurationChangelog.filters.modifierId',
      }),
      getOptionLabel: getModifierLabel,
      getChipLabel: (value: string[]) => value.map(getModifierLabel).join(', '),
      valueGetter: (currentValue: ConfigurationChangelogQuery) =>
        currentValue?.modifierId?.map(String),
      setFilter: (value: string[] | undefined) =>
        setFilters({
          modifierId: value?.length ? value.map(Number) : undefined,
        }),
      Component: FilterMultiSelect,
    }),
    createSettings({
      filterKey: 'objectId',
      options: [],
      name: intl.formatMessage({
        id: 'features.configurationChangelog.filters.objectId',
      }),
      getChipLabel: (value: number[]) => value.join(', '),
      valueGetter: (currentValue: ConfigurationChangelogQuery) =>
        currentValue?.objectId,
      setFilter: (value: number[] | undefined) =>
        setGenericObjectFilter({ objectId: value }),
      Component: FilterNumberValues,
    }),
  ];
};
